import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';
import { DraftFunction } from 'use-immer';

import Location from '@r6digital/rapidstor-types/react/entities/Location';
import InsuranceCoverage from '@r6digital/rapidstor-types/types/InsuranceCoverage';

import { WizardValidationError } from '@/types/Wizard';
import { RapidStorSettings } from '@/utils/rapidstor';

import Button from '@/components/elements/button';

import classes from './styles.module.scss';

import Boxes from './boxes';
import Picker from './picker';

interface Props {
    coverages: InsuranceCoverage[] | undefined;
    update: (arg: Record<string, string> | DraftFunction<Record<string, string>>) => void;
    currentlySelected: number;
    form: Record<string, string>;
    validationErrors: WizardValidationError[];
    updateReservation: (doCostRetrieve: boolean, insuranceCoverage?: string) => void;
    taxRate: number;
}

export default function InsuranceCoverageComponent({
    coverages,
    update,
    currentlySelected,
    form,
    validationErrors,
    updateReservation,
    taxRate
}: Props) {
    const location = useLoaderData() as Location;
    const { t } = useTranslation();
    const initialized = useRef(false);

    const [selectedCoverage, setSelectedCoverage] = useState<number>(-1);
    const [timer, setTimer] = useState<NodeJS.Timeout>();
    const [modalOpen, setModalOpen] = useState<boolean>(false);

    const handleButtonClick = useCallback(
        (coverage: number) => {
            if (!coverages || !coverages.length) {
                return;
            }
            if (timer) {
                clearTimeout(timer);
            }

            setTimer(
                setTimeout(() => {
                    setSelectedCoverage(coverage);
                    const newCoverage = coverage === -1 ? '' : `${coverages[coverage]._id}`;
                    update((draft) => {
                        draft.insuranceCoverage = newCoverage;

                        if (RapidStorSettings(location).insurance().manualEntry().show() && newCoverage) {
                            draft.declaredValueOfGoods = coverages[coverage].dcCoverage.toString();
                            return
                        }
                    });
                    updateReservation(true, newCoverage);
                }, 2000)
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [coverages, timer, update]
    );

    const handleValueChange = useCallback(
        (value: string) => {
            if (!coverages || !coverages.length || Number(value) === 0 || !value) {
                return;
            }
            if (timer) {
                clearTimeout(timer);
            }

            setTimer(
                setTimeout(() => {
                    const index = coverages.findIndex(
                        (coverage) => coverage.dcCoverage >= Number(value)
                    );

                    if (index === -1) {
                        setSelectedCoverage(coverages.length - 1);
                        update((draft) => {
                            draft.insuranceCoverage = `${coverages[coverages.length - 1]._id
                                }`;
                        });
                        updateReservation(true, coverages[coverages.length - 1]._id);
                    } else {
                        setSelectedCoverage(index);
                        const newCoverage = index === -1 ? '' : `${coverages[index]._id}`;
                        update((draft) => {
                            draft.insuranceCoverage = newCoverage;
                        });
                        updateReservation(true, newCoverage);
                    }
                }, 2000)
            );
        },
        [coverages, timer, update, updateReservation]
    );

    const formErrors = useMemo(() => {
        let inputNames: string[] = [];
        inputNames = [
            'declaredValueOfGoods',
            'declaredValueOfGoodsAgree',
            'insuranceCoverage',
        ];

        return validationErrors?.filter((error) => inputNames.includes(error.name));
    }, [validationErrors]);

    useEffect(() => {
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [timer]);

    useEffect(() => {
        if (coverages && coverages.length && !initialized.current) {
            const coverage = currentlySelected === -1 ? '' : `${coverages[currentlySelected]._id}`;
            if (currentlySelected === -1) return;
            setSelectedCoverage(currentlySelected);
            update((draft) => {
                draft.insuranceCoverage = coverage;
            });
            updateReservation(true, coverage);
            initialized.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentlySelected]);

    return (
        <div className={`${classes.form}`}>
            <div className={`${classes.field}`}>
                <div className={`${classes.title}`}>
                    <span className={`${classes.header_text}`}>
                        {t('wizard:insuranceCoverage:title')}
                    </span>

                    {location.rapidstor.wizards.moveinSingle
                        .insuranceContentPopoverText && (
                            <div
                                onClick={() => setModalOpen(true)}
                                className={`${classes.popover}`}
                            >
                                <svg
                                    width='21'
                                    height='20'
                                    viewBox='0 0 21 20'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                >
                                    <path d='M10.5 0C8.52219 0 6.58879 0.58649 4.9443 1.6853C3.29981 2.78412 2.01809 4.3459 1.26121 6.17316C0.504333 8.00042 0.3063 10.0111 0.692152 11.9509C1.078 13.8907 2.03041 15.6725 3.42894 17.0711C4.82746 18.4696 6.60929 19.422 8.5491 19.8078C10.4889 20.1937 12.4996 19.9957 14.3268 19.2388C16.1541 18.4819 17.7159 17.2002 18.8147 15.5557C19.9135 13.9112 20.5 11.9778 20.5 10C20.4972 7.34869 19.4427 4.80678 17.568 2.93202C15.6932 1.05727 13.1513 0.00279983 10.5 0ZM10.5 18.4615C8.82647 18.4615 7.19052 17.9653 5.79902 17.0355C4.40753 16.1057 3.323 14.7842 2.68256 13.2381C2.04213 11.6919 1.87456 9.99061 2.20105 8.34923C2.52754 6.70786 3.33343 5.20016 4.51679 4.01679C5.70016 2.83342 7.20786 2.02754 8.84924 1.70105C10.4906 1.37456 12.1919 1.54212 13.7381 2.18256C15.2842 2.82299 16.6057 3.90753 17.5355 5.29902C18.4653 6.69051 18.9615 8.32646 18.9615 10C18.959 12.2434 18.0667 14.3941 16.4804 15.9804C14.8941 17.5667 12.7434 18.459 10.5 18.4615ZM12.0385 14.6154C12.0385 14.8194 11.9574 15.0151 11.8132 15.1593C11.6689 15.3036 11.4732 15.3846 11.2692 15.3846C10.8612 15.3846 10.4699 15.2225 10.1814 14.934C9.89286 14.6455 9.73077 14.2542 9.73077 13.8462V10C9.52676 10 9.3311 9.91895 9.18684 9.7747C9.04259 9.63044 8.96154 9.43478 8.96154 9.23077C8.96154 9.02675 9.04259 8.8311 9.18684 8.68684C9.3311 8.54258 9.52676 8.46154 9.73077 8.46154C10.1388 8.46154 10.5301 8.62362 10.8186 8.91214C11.1071 9.20066 11.2692 9.59197 11.2692 10V13.8462C11.4732 13.8462 11.6689 13.9272 11.8132 14.0715C11.9574 14.2157 12.0385 14.4114 12.0385 14.6154ZM8.96154 5.76923C8.96154 5.54102 9.02921 5.31794 9.156 5.12819C9.28279 4.93844 9.46299 4.79055 9.67383 4.70321C9.88467 4.61588 10.1167 4.59303 10.3405 4.63755C10.5643 4.68208 10.7699 4.79197 10.9313 4.95334C11.0926 5.11471 11.2025 5.3203 11.2471 5.54413C11.2916 5.76795 11.2687 5.99995 11.1814 6.21079C11.0941 6.42163 10.9462 6.60183 10.7564 6.72862C10.5667 6.8554 10.3436 6.92308 10.1154 6.92308C9.80937 6.92308 9.51588 6.80151 9.29949 6.58512C9.08311 6.36873 8.96154 6.07525 8.96154 5.76923Z' />
                                </svg>
                            </div>
                        )}
                </div>
                <span
                    className={`${classes.subtitle}`}
                    dangerouslySetInnerHTML={{
                        __html: location.rapidstor.wizards.moveinSingle.insuranceContent,
                    }}
                ></span>
            </div>

            {!coverages || !coverages.length ? (
                <ContentLoader
                    foregroundColor={`${location.skin.mainColour}`}
                    backgroundColor='#fff'
                    width={'100%'}
                    height={'120'}
                >
                    <rect x='0' y='0' rx='4' ry='4' width='100%' height='120' />
                </ContentLoader>
            ) : location.rapidstor.wizards.moveinSingle.hideInsurancePicker ? (
                <Boxes
                    selectedCoverage={selectedCoverage}
                    formErrors={formErrors}
                    coverages={coverages}
                    handleValueChange={handleValueChange}
                    form={form}
                    update={update}
                    taxRate={taxRate}
                />
            ) : (
                <Picker
                    selectedCoverage={selectedCoverage}
                    setSelectedCoverage={setSelectedCoverage}
                    handleButtonClick={handleButtonClick}
                    formErrors={formErrors}
                    coverages={coverages}
                    handleValueChange={handleValueChange}
                    form={form}
                    update={update}
                    taxRate={taxRate}
                />
            )}

            <div
                className={`${classes.modal} ${modalOpen ? classes.open : classes.closed
                    }`}
            >
                <div className={`${classes.dialog}`}>
                    <div className={`${classes.body}`}>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: location.rapidstor.wizards.moveinSingle
                                    .insuranceContentPopoverText,
                            }}
                        ></div>
                    </div>
                    <div className={`${classes.footer}`}>
                        <Button
                            type={`primary`}
                            size={`medium`}
                            onclick={() => setModalOpen(false)}
                            disableScroll
                        >
                            {t('cta:close')}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
